/**
 * Reads the cell text of an Office Open XML workbook (`.xlsx`) in the browser, so the approved
 * COA workbook can be uploaded as it is (FRD-FIN-015) rather than retyped as JSON.
 *
 * Deliberately small: an `.xlsx` is a ZIP of XML parts, so this reads the ZIP directory, inflates
 * the parts it needs with the platform's own `DecompressionStream`, and reads the shared strings
 * and each sheet's cells. It evaluates no formula (it reads the cached value Excel stored) and
 * reads no style except to recognise a date. Nothing is sent anywhere: the import API still
 * validates every value (FRD-FIN-016).
 */

export type Sheet = readonly (readonly string[])[];

const EOCD = 0x06054b50;
const CENTRAL = 0x02014b50;
const LOCAL = 0x04034b50;

interface Entry {
  readonly name: string;
  readonly method: number;
  readonly compressedSize: number;
  readonly localOffset: number;
}

function entries(view: DataView): Map<string, Entry> {
  let eocd = -1;
  for (let i = view.byteLength - 22; i >= Math.max(0, view.byteLength - 65_557); i -= 1) {
    if (view.getUint32(i, true) === EOCD) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('This file is not an .xlsx workbook.');
  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const decoder = new TextDecoder();
  const out = new Map<string, Entry>();
  for (let n = 0; n < count; n += 1) {
    if (view.getUint32(offset, true) !== CENTRAL) throw new Error('The workbook is damaged.');
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);
    const name = decoder.decode(
      new Uint8Array(view.buffer, view.byteOffset + offset + 46, nameLength),
    );
    out.set(name, { name, method, compressedSize, localOffset });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return out;
}

async function readEntry(view: DataView, entry: Entry): Promise<string> {
  if (view.getUint32(entry.localOffset, true) !== LOCAL)
    throw new Error('The workbook is damaged.');
  const nameLength = view.getUint16(entry.localOffset + 26, true);
  const extraLength = view.getUint16(entry.localOffset + 28, true);
  const start = view.byteOffset + entry.localOffset + 30 + nameLength + extraLength;
  const bytes = new Uint8Array(view.buffer, start, entry.compressedSize);
  if (entry.method === 0) return new TextDecoder().decode(bytes);
  if (entry.method !== 8) throw new Error('The workbook uses an unsupported compression.');
  const source = new ReadableStream<BufferSource>({
    start(controller) {
      controller.enqueue(bytes.slice());
      controller.close();
    },
  });
  const stream = source.pipeThrough(new DecompressionStream('deflate-raw'));
  return new Response(stream).text();
}

function xml(text: string): Document {
  return new DOMParser().parseFromString(text, 'application/xml');
}

/** Elements by local name, whatever namespace prefix the part uses. */
function byName(node: Document | Element, name: string): Element[] {
  return Array.from(node.getElementsByTagNameNS('*', name));
}

/** The column index of a cell reference such as `AC12` (A = 0). */
function columnOf(ref: string): number {
  let column = 0;
  for (const char of ref) {
    if (char < 'A' || char > 'Z') break;
    column = column * 26 + (char.charCodeAt(0) - 64);
  }
  return column - 1;
}

/** Excel's serial day number as `YYYY-MM-DD` (1900 date system). */
function serialDate(serial: number): string {
  const ms = Math.round((serial - 25_569) * 86_400_000);
  return new Date(ms).toISOString().slice(0, 10);
}

/** The built-in number formats that are dates, plus any custom one whose code looks like a date. */
function dateStyles(stylesXml: string | undefined): Set<number> {
  const out = new Set<number>();
  if (stylesXml === undefined) return out;
  const doc = xml(stylesXml);
  const custom = new Map<number, string>();
  for (const fmt of byName(doc, 'numFmt')) {
    custom.set(Number(fmt.getAttribute('numFmtId')), fmt.getAttribute('formatCode') ?? '');
  }
  const cellXfs = byName(doc, 'cellXfs')[0];
  const xfs = cellXfs === undefined ? [] : byName(cellXfs, 'xf');
  xfs.forEach((xf, index) => {
    const id = Number(xf.getAttribute('numFmtId'));
    const code = custom.get(id) ?? '';
    if (
      (id >= 14 && id <= 22) ||
      (id >= 45 && id <= 47) ||
      /[dy]/i.test(code.replace(/"[^"]*"/g, ''))
    ) {
      out.add(index);
    }
  });
  return out;
}

/** Every sheet of the workbook by name, each as rows of cell text. */
export async function readWorkbook(data: ArrayBuffer): Promise<Map<string, Sheet>> {
  const view = new DataView(data);
  const zip = entries(view);
  const part = async (name: string) => {
    const entry = zip.get(name);
    return entry === undefined ? undefined : readEntry(view, entry);
  };
  const workbook = await part('xl/workbook.xml');
  const rels = await part('xl/_rels/workbook.xml.rels');
  if (workbook === undefined || rels === undefined) {
    throw new Error('This file is not an .xlsx workbook.');
  }
  const shared: string[] = [];
  const sharedXml = await part('xl/sharedStrings.xml');
  if (sharedXml !== undefined) {
    for (const si of byName(xml(sharedXml), 'si')) {
      shared.push(
        byName(si, 't')
          .map((t) => t.textContent)
          .join(''),
      );
    }
  }
  const dates = dateStyles(await part('xl/styles.xml'));
  const targets = new Map<string, string>();
  for (const rel of byName(xml(rels), 'Relationship')) {
    targets.set(rel.getAttribute('Id') ?? '', rel.getAttribute('Target') ?? '');
  }
  const sheets = new Map<string, Sheet>();
  for (const sheet of byName(xml(workbook), 'sheet')) {
    const relId =
      sheet.getAttributeNS(
        'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
        'id',
      ) ?? sheet.getAttribute('r:id');
    const target = targets.get(relId ?? '');
    if (target === undefined) continue;
    const path = target.startsWith('/') ? target.slice(1) : `xl/${target}`;
    const text = await part(path);
    if (text === undefined) continue;
    const rows: string[][] = [];
    for (const row of byName(xml(text), 'row')) {
      const index = Number(row.getAttribute('r')) - 1;
      const cells: string[] = [];
      for (const cell of byName(row, 'c')) {
        const column = columnOf(cell.getAttribute('r') ?? 'A');
        const type = cell.getAttribute('t');
        const raw = byName(cell, 'v')[0]?.textContent ?? '';
        let value: string;
        if (type === 's') value = shared[Number(raw)] ?? '';
        else if (type === 'inlineStr')
          value = byName(cell, 't')
            .map((t) => t.textContent)
            .join('');
        else if (type === 'b') value = raw === '1' ? 'TRUE' : 'FALSE';
        else if (raw !== '' && type !== 'str' && dates.has(Number(cell.getAttribute('s') ?? -1))) {
          value = serialDate(Number(raw));
        } else value = raw;
        cells[column] = value.trim();
      }
      rows[index] = dense(cells);
    }
    sheets.set(sheet.getAttribute('name') ?? '', dense(rows, [] as string[]));
  }
  return sheets;
}

/** A sparse array (cells or rows the sheet omits) as a dense one, holes filled with `blank`. */
function dense<T>(values: readonly T[], blank?: T): T[] {
  const out: T[] = [];
  for (let i = 0; i < values.length; i += 1) {
    out.push(i in values ? (values[i] as T) : (blank ?? ('' as T)));
  }
  return out;
}
